import React, { useEffect, useState, useCallback } from 'react';
import {
  collection, query, orderBy, getDocs,
  addDoc, serverTimestamp, where,
} from 'firebase/firestore';
import { db } from '../firebase';
import MediaCard from '../components/MediaCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';

const TMDB_BASE = 'https://api.themoviedb.org/3';
const POSTER_BASE = 'https://image.tmdb.org/t/p/w500';

function Requests({ user, isAdmin }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const tmdbKey = process.env.REACT_APP_TMDB_API_KEY;

  const fetchRequests = useCallback(async () => {
    try {
      const q = query(collection(db, 'requests'), orderBy('requestedAt', 'desc'));
      const snap = await getDocs(q);
      setRequests(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    if (!tmdbKey) {
      setSearchError('TMDB API key is not configured.');
      return;
    }
    setSearching(true);
    setSearchError('');
    setSearchResults([]);
    try {
      const res = await fetch(
        `${TMDB_BASE}/search/multi?query=${encodeURIComponent(searchQuery)}&api_key=${tmdbKey}&page=1`
      );
      if (!res.ok) throw new Error('TMDB request failed');
      const data = await res.json();
      const results = (data.results || [])
        .filter(r => r.media_type === 'movie' || r.media_type === 'tv')
        .slice(0, 24)
        .map(r => ({
          tmdbId: r.id,
          type: r.media_type,
          title: r.title || r.name,
          posterPath: r.poster_path,
          overview: r.overview,
          releaseDate: r.release_date || r.first_air_date,
        }));
      setSearchResults(results);
      if (results.length === 0) setSearchError('No results found.');
    } catch (err) {
      setSearchError('Search failed. Please try again.');
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const handleRequest = async (item) => {
    setSubmitting(item.tmdbId);
    setSuccessMsg('');
    try {
      const q = query(
        collection(db, 'requests'),
        where('tmdbId', '==', item.tmdbId),
        where('type', '==', item.type)
      );
      const existing = await getDocs(q);
      if (!existing.empty) {
        setSuccessMsg(`"${item.title}" has already been requested.`);
        setSubmitting(null);
        return;
      }
      await addDoc(collection(db, 'requests'), {
        tmdbId: item.tmdbId,
        type: item.type,
        title: item.title,
        posterPath: item.posterPath || null,
        overview: item.overview || '',
        releaseDate: item.releaseDate || null,
        requestedBy: user.email,
        requestedAt: serverTimestamp(),
        status: 'pending',
      });
      setSuccessMsg(`"${item.title}" has been requested!`);
      fetchRequests();
    } catch (err) {
      console.error('Request failed:', err);
      setSuccessMsg('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(null);
    }
  };

  const myRequests = requests.filter(r => r.requestedBy === user.email);
  const othersRequests = requests.filter(r => r.requestedBy !== user.email);

  const requestedIds = new Set(requests.map(r => `${r.type}-${r.tmdbId}`));

  const statusColor = (status) => {
    if (status === 'added' || status === 'fulfilled') return 'success';
    if (status === 'coming_soon') return 'warning';
    if (status === 'denied') return 'error';
    return 'default';
  };

  const statusLabel = (status) => {
    if (status === 'coming_soon') return 'Coming Soon';
    if (status === 'added') return 'Added';
    if (!status) return 'Pending';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Requests</Typography>

      {/* Search Form */}
      <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', gap: 1.5, mb: 3.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search for a movie or TV show…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          sx={{ flex: '1 1 260px' }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={searching || !searchQuery.trim()}
          startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
        >
          {searching ? 'Searching…' : 'Search'}
        </Button>
      </Box>

      {successMsg && <Alert severity="info" sx={{ mb: 2 }}>{successMsg}</Alert>}
      {searchError && <Alert severity="error" sx={{ mb: 2 }}>{searchError}</Alert>}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
            Search Results
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
            {searchResults.map(item => {
              const alreadyRequested = requestedIds.has(`${item.type}-${item.tmdbId}`);
              return (
                <MediaCard
                  key={`${item.type}-${item.tmdbId}`}
                  item={item}
                  actions={
                    <Button
                      size="small"
                      variant={alreadyRequested ? 'outlined' : 'contained'}
                      fullWidth
                      disabled={alreadyRequested || submitting === item.tmdbId}
                      onClick={() => handleRequest(item)}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {alreadyRequested ? 'Requested' : submitting === item.tmdbId ? '…' : 'Request'}
                    </Button>
                  }
                />
              );
            })}
          </Box>
        </Box>
      )}

      {/* My Requests */}
      <Typography variant="h6" sx={{ mb: 2 }}>My Requests</Typography>
      {loadingRequests ? (
        <LoadingSpinner message="Loading requests…" />
      ) : myRequests.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 4 }}>You haven't made any requests yet.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 4 }}>
          {myRequests.map(req => (
            <Paper key={req.id} sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              {req.posterPath ? (
                <Avatar
                  variant="rounded"
                  src={`${POSTER_BASE}${req.posterPath}`}
                  alt={req.title}
                  sx={{ width: 40, height: 60, flexShrink: 0 }}
                />
              ) : (
                <Avatar variant="rounded" sx={{ width: 40, height: 60, flexShrink: 0 }}>🎬</Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{req.title}</Typography>
                  <Chip
                    label={req.type === 'tv' ? 'TV' : 'Movie'}
                    size="small"
                    color={req.type === 'tv' ? 'secondary' : 'primary'}
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                  <Chip
                    label={statusLabel(req.status)}
                    size="small"
                    color={statusColor(req.status)}
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Requested by you
                  {req.requestedAt?.toDate && (
                    <> · {req.requestedAt.toDate().toLocaleDateString()}</>
                  )}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Requests from Others */}
      <Typography variant="h6" sx={{ mb: 2 }}>Requests from Others</Typography>
      {loadingRequests ? (
        <LoadingSpinner message="Loading requests…" />
      ) : othersRequests.length === 0 ? (
        <Typography color="text.secondary">No requests from others yet.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {othersRequests.map(req => (
            <Paper key={req.id} sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              {req.posterPath ? (
                <Avatar
                  variant="rounded"
                  src={`${POSTER_BASE}${req.posterPath}`}
                  alt={req.title}
                  sx={{ width: 40, height: 60, flexShrink: 0 }}
                />
              ) : (
                <Avatar variant="rounded" sx={{ width: 40, height: 60, flexShrink: 0 }}>🎬</Avatar>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{req.title}</Typography>
                  <Chip
                    label={req.type === 'tv' ? 'TV' : 'Movie'}
                    size="small"
                    color={req.type === 'tv' ? 'secondary' : 'primary'}
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                  <Chip
                    label={statusLabel(req.status)}
                    size="small"
                    color={statusColor(req.status)}
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {isAdmin ? (
                    <>Requested by {req.requestedBy}</>
                  ) : (
                    <>Requested by another user</>
                  )}
                  {req.requestedAt?.toDate && (
                    <> · {req.requestedAt.toDate().toLocaleDateString()}</>
                  )}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Container>
  );
}

export default Requests;
